import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt, faTrashAlt, faSync, faCompress, faExpand, faNoteSticky, faShare, faComments, faCog, faChartLine, faFileSignature, faPlus, faDownload, faUsers, faFileAlt, faClock, faDollarSign, faUser, faSpinner, faExclamationTriangle, faCaretDown, faBook, faClipboardList, faUserTie, faFileContract, faRotate, faPhone } from '@fortawesome/free-solid-svg-icons';
import { API_ENDPOINTS } from '../../config/api';
import axios from 'axios';
import './styles/LeadTracker.css';
import './styles/Contracting.css';
import { 
  ClientSideRowModelModule,
  ModuleRegistry
} from 'ag-grid-community';
import NotesModal from './NotesModal';
import EditModal from './EditModal';
import AlgorithmControls from './AlgorithmControls';
import { Link, useNavigate } from 'react-router-dom';
import Dashboard from './Dashboard';
import ContractModal from './ContractModal';
import TaskGrid from '../TaskManager/TaskGrid';
import UploadTracker from '../UploadTracker/UploadTracker';
import TikTokDownloadModal from './TikTokDownloadModal';
import PartnersModal from './PartnersModal';
import { signOut } from 'aws-amplify/auth';
import AnalyticsDashboard from '../Analytics/AnalyticsDashboard';
import AdminSurvey from '../survey/AdminDashboard';
import EmployeeDashboard from '../Employee/EmployeeDashboard';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import TaskTemplate from '../TaskTemplate/TaskTemplate';
import RFPManager from '../RFPManager/RFPManager';
import FormsManager from '../Forms/FormsManager';
import PartnerEmployeeModal from './PartnerEmployeeModal';
import { canAccessFeature, FEATURES, filterClientsByRole, shouldFilterByAssignedClients, shouldFilterUnsignedOnly } from '../../utils/permissions';
import ContactsModal from './ContactsModal';
import MassMessaging from './MassMessaging';
import TikTokViewer from '../TikTokViewer/TikTokViewer';
import GoogleMeetModal from './GoogleMeetModal';
import NotificationsPage from '../Notifications/NotificationsPage';
import StaffNotificationManager from '../Notifications/StaffNotificationManager';
import OnboardingProgressBar from './OnboardingProgressBar';
import OnboardingModal from './OnboardingModal';
import AutomationManager from '../Automations/AutomationManager';
import RolePermissionsManager from '../Admin/RolePermissionsManager';

// Define base URL directly to ensure it's available
const API_BASE_URL = 'https://track.snapped.cc';

// Register the required modules
ModuleRegistry.registerModules([
  ClientSideRowModelModule
]);

// Add this after your imports, before the EditModal component
axios.interceptors.request.use(async (config) => {
  try {
    const { tokens } = await fetchAuthSession();
    const token = tokens.idToken.toString();
    config.headers.Authorization = `Bearer ${token}`;
  } catch (error) {
    console.error('Error getting auth token:', error);
  }
  return config;
});

const SocialLinksModal = ({ isOpen, onClose, data }) => {
  if (!isOpen) return null;

  console.log('Modal Data:', data); // Debug log
  
  const socialLinks = [
    { 
      platform: 'Instagram', 
      username: data?.IG_Username,
      baseUrl: 'https://www.instagram.com/'
    },
    { 
      platform: 'TikTok', 
      username: data?.TT_Username,
      baseUrl: 'https://www.tiktok.com/@'
    },
    { 
      platform: 'YouTube', 
      username: data?.YT_Username,
      baseUrl: 'https://www.youtube.com/'
    },
    { 
      platform: 'Snapchat', 
      username: data?.Snap_Username,
      baseUrl: 'https://snapchat.com/add/'
    }
  ].filter(link => link.username);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="social-links-modal" onClick={e => e.stopPropagation()}>
        <h3>Social Links</h3>
        <div className="social-links-list">
          {socialLinks.map(({ platform, username, baseUrl }) => (
            <a 
              key={platform}
              href={`${baseUrl}${username}`}
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
            >
              {platform}
            </a>
          ))}
        </div>
        <button onClick={onClose} className="close-button">Close</button>
      </div>
    </div>
  );
};

const EmployeesSheet = () => {
  return (
    <div className="google-sheet-container">
      <iframe
        src="https://docs.google.com/spreadsheets/d/1HRG1QJxk8QW9KtDHoAGDHmXeagnjcR-GzBs8Aq4hWGw/edit?usp=sharing"
        style={{ width: '100%', height: '100vh', border: 'none' }}
        allowFullScreen={true}
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        loading="lazy"
      />
    </div>
  );
};

const PreLeadsSheet = () => {
  return (
    <div className="google-sheet-container">
      <iframe
        src="https://docs.google.com/spreadsheets/d/15bWel78i-yKT0kHU7xO0ZeImBWBd7UD30wr09lyXSXI/edit?usp=sharing"
        style={{ width: '100%', height: '100vh', border: 'none' }}
        allowFullScreen={true}
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        loading="lazy"
      />
    </div>
  );
};

const OnboardingSheet = () => {
  return (
    <div className="google-sheet-container">
      <iframe
        src="https://docs.google.com/spreadsheets/d/1y-KBq1Nh6nZfeTs5-8HPDorQrJH2aNrbfu-VbeYrN4o/edit?usp=sharing"
        style={{ width: '100%', height: '100vh', border: 'none' }}
        allowFullScreen={true}
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        loading="lazy"
      />
    </div>
  );
};

const SystemsSheet = () => {
  return (
    <div className="google-sheet-container">
      <iframe
        src="https://docs.google.com/spreadsheets/d/1WPYxKaj98fz1pvXvmtTQ_g0thFjGI8fXo4j9tZnFv0U/edit?usp=sharing"
        style={{ width: '100%', height: '100vh', border: 'none' }}
        allowFullScreen={true}
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        loading="lazy"
      />
    </div>
  );
};

const TalentPayoutsSheet = () => {
  return (
    <div className="google-sheet-container">
      <iframe
        src="https://docs.google.com/spreadsheets/d/1qE9rDC9M7_bQqN6MwMS8HWQEksA8sdWc4N7yVcnag1g/edit?usp=sharing"
        style={{ width: '100%', height: '100vh', border: 'none' }}
        allowFullScreen={true}
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        loading="lazy"
      />
    </div>
  );
};

// Update the tab filters mapping at the top level
const TAB_FILTERS = {
  'All': null,
  'Leads': 'unsigned',
  'All Signed': 'is_signed',
  'Pending Form': 'pending_form',
  'Unmonetized': 'unmonetized',
  'Monetized + Verified': 'monetized_verified',
  'Dead': 'is_dead',
  'New': 'new_signed',
  '7 Days': 'seven_days_signed'
};

const LeadTracker = () => {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [leadsData, setLeadsData] = useState([]);
  const [gridApi, setGridApi] = useState(null);
  const [columnApi, setColumnApi] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeTab, setActiveTab] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [socialLinksModalOpen, setSocialLinksModalOpen] = useState(false);
  const [selectedSocialData, setSelectedSocialData] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [showAlgorithmControls, setShowAlgorithmControls] = useState(false);
  
  // Set default view based on user role
  const getDefaultView = useCallback(() => {
    if (!user?.role) return 'DASHBOARD';
    
    // Editor role should default to UPLOADS (their primary feature)
    if (user.role === 'editor') return 'UPLOADS';
    
    // Sales role should default to CLIENTS
    if (user.role === 'sales') return 'CLIENTS';
    
    // All others (admin, csm, manager) default to DASHBOARD
    return 'DASHBOARD';
  }, [user?.role]);
  
  const [currentView, setCurrentView] = useState(getDefaultView());
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [selectedContractClient, setSelectedContractClient] = useState(null);
  const [tiktokModalOpen, setTiktokModalOpen] = useState(false);
  const [selectedTiktokClient, setSelectedTiktokClient] = useState(null);
  const [googleMeetModalOpen, setGoogleMeetModalOpen] = useState(false);
  const [selectedMeetClient, setSelectedMeetClient] = useState(null);
  const [onboardingModalOpen, setOnboardingModalOpen] = useState(false);
  const [selectedOnboardingClient, setSelectedOnboardingClient] = useState(null);
  const [onboardingStatuses, setOnboardingStatuses] = useState({});
  const [showOnboardingText, setShowOnboardingText] = useState(true);
  const [partners, setPartners] = useState([]);
  const [showPartnersModal, setShowPartnersModal] = useState(false);
  const [showPartnerEmployeeModal, setShowPartnerEmployeeModal] = useState(false);
  const [showContactsModal, setShowContactsModal] = useState(false);
  const [partnerEmployeeAssignments, setPartnerEmployeeAssignments] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [totalViews, setTotalViews] = useState({
    all: 0,
    filtered: 0,
    loading: false,
    sevenDay: 0,
    sevenDayFiltered: 0
  });
  const [rankData, setRankData] = useState({});
  const [partnersLoading, setPartnersLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSheetDropdownOpen, setIsSheetDropdownOpen] = useState(false);
  const navigate = useNavigate();
  // Remove cooldown period
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [docsMenuOpen, setDocsMenuOpen] = useState(false);
  const [settingsDropdownOpen, setSettingsDropdownOpen] = useState(false);
  const [processingState, setProcessingState] = useState({
    loading: false,
    currentStep: '',
    progress: {
      total: 0,
      current: 0
    }
  });
  const moreMenuRef = useRef(null);
  const docsMenuRef = useRef(null);
  const settingsDropdownRef = useRef(null);
  // Preserve grid scroll/position across modal open/close
  const gridScrollRef = useRef({ rowIndex: null, scrollTop: null });

  const saveGridScrollPosition = useCallback(() => {
    try {
      if (!gridApi) return;
      // Get the first displayed row using getDisplayedRowAtIndex
      let rowIndex = null;
      const displayedRowCount = gridApi.getDisplayedRowCount();
      if (displayedRowCount > 0) {
        const firstNode = gridApi.getDisplayedRowAtIndex(0);
        if (firstNode) {
          // Get the actual row index by finding which row is currently visible at the top
          const viewport = document.querySelector('.ag-center-cols-viewport') || document.querySelector('.ag-body-viewport');
          if (viewport && viewport.scrollTop > 0) {
            // Estimate which row is at the top based on scroll position
            const rowHeight = gridApi.getSizesForCurrentTheme().rowHeight || 42;
            rowIndex = Math.floor(viewport.scrollTop / rowHeight);
          } else {
            rowIndex = 0;
          }
        }
      }
      const viewport = document.querySelector('.ag-center-cols-viewport') || document.querySelector('.ag-body-viewport');
      const scrollTop = viewport ? viewport.scrollTop : null;
      gridScrollRef.current = { rowIndex, scrollTop };
    } catch (e) {
      console.error('Failed to save grid scroll position:', e);
    }
  }, [gridApi]);

  const restoreGridScrollPosition = useCallback(() => {
    try {
      if (!gridApi) return;
      const { rowIndex, scrollTop } = gridScrollRef.current || {};
      if (rowIndex != null && rowIndex >= 0) {
        gridApi.ensureIndexVisible(rowIndex, 'top');
      }
      const viewport = document.querySelector('.ag-center-cols-viewport') || document.querySelector('.ag-body-viewport');
      if (viewport && scrollTop != null) {
        viewport.scrollTop = scrollTop;
      }
    } catch (e) {
      console.error('Failed to restore grid scroll position:', e);
    }
  }, [gridApi]);

  // Define handleTabClick once at the top level
  const handleTabClick = useCallback((tab) => {
    console.log('Tab changed to:', tab);
    setActiveTab(tab);
    // Reset search when changing tabs
    setSearchTerm('');
  }, []);

  // Memoized filtered data calculation
  const filteredData = useMemo(() => {
    console.log('Starting filter calculation:', {
      rowDataLength: rowData?.length || 0,
      leadsDataLength: leadsData?.length || 0,
      activeTab,
      searchTerm,
      tabFilter: TAB_FILTERS[activeTab],
      userRole: user?.role
    });

    // Always use rowData (clients) for all tabs now
    let dataToFilter = rowData;
    
    if (!dataToFilter || !Array.isArray(dataToFilter)) {
      console.log('No valid data for', activeTab);
      return [];
    }

    let filtered = [...dataToFilter];
    console.log('Initial data count:', filtered.length);

    // Apply role-based filtering first
    if (user?.role) {
      const userId = user?.userId || user?.username;
      filtered = filterClientsByRole(filtered, user.role, userId);
      console.log('After role-based filter:', filtered.length);
    }

    // Apply tab filters
    if (activeTab === 'All') {
      // Show all clients except dead ones
      filtered = filtered.filter(row => !row.is_dead);
      console.log('All clients filter - showing all except dead');
    } else if (activeTab === 'Leads') {
      // Show only unsigned clients (is_signed is empty, false, or not present)
      filtered = filtered.filter(row => !row.is_dead && !row.is_signed);
      console.log('After unsigned leads filter:', filtered.length);
    } else if (activeTab === 'All Signed') {
      // Show all signed clients (is_signed === true)
      filtered = filtered.filter(row => !row.is_dead && row.is_signed === true);
      console.log('After all signed filter:', filtered.length);
    } else if (activeTab === 'Pending Form') {
      // SHOW if onboard form was sent but NOT submitted yet
      // Exclude anyone who is already monetized
      filtered = filtered.filter(row => 
        !row.is_dead && 
        row.onboard_form_sent === true &&
        row.onboard_form_completed !== true &&
        row.Snap_Monetized !== true
      );
      console.log('After pending form filter:', filtered.length);
    } else if (activeTab === 'Unmonetized') {
      // Show signed but not monetized (is_signed && !Snap_Monetized)
      filtered = filtered.filter(row => 
        !row.is_dead && 
        row.is_signed === true && 
        !row.Snap_Monetized
      );
      console.log('After unmonetized filter:', filtered.length);
    } else if (activeTab === 'Monetized + Verified') {
      // Show signed, monetized, and verified
      filtered = filtered.filter(row => 
        !row.is_dead && 
        row.is_signed === true && 
        row.Snap_Monetized === true && 
        row.Snap_Star === true
      );
      console.log('After monetized + verified filter:', filtered.length);
    } else if (activeTab === 'Dead') {
      // Show only dead leads
      filtered = filtered.filter(row => row.is_dead === true);
      console.log('After dead leads filter:', filtered.length);
    } else if (activeTab === 'New') {
      // Show clients signed in the last two weeks (14 days)
      const twoWeeksAgo = new Date();
      twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
      
      filtered = filtered.filter(row => {
        if (!row.is_dead && row.is_signed === true) {
          // Check if there's a signed_date or created_at field
          const signedDate = row.signed_date || row.created_at;
          if (signedDate) {
            const rowDate = new Date(signedDate);
            return rowDate >= twoWeeksAgo;
          }
        }
        return false;
      });
      console.log('After new signed filter (last 14 days):', filtered.length);
    } else if (activeTab === '7 Days') {
      // Show clients signed in the last 7 days
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      filtered = filtered.filter(row => {
        if (!row.is_dead && row.is_signed === true) {
          // Check if there's a signed_date or created_at field
          const signedDate = row.signed_date || row.created_at;
          if (signedDate) {
            const rowDate = new Date(signedDate);
            return rowDate >= sevenDaysAgo;
          }
        }
        return false;
      });
      console.log('After 7 days signed filter:', filtered.length);
    }

    // Apply search filter if exists
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      console.log('Applying search filter:', searchLower);
      filtered = filtered.filter(row => {
        const searchFields = [
          'First_Legal_Name',
          'Last_Legal_Name',
          'Email_Address',
          'Stage_Name',
          'IG_Username',
          'TT_Username',
          'YT_Username',
          'Snap_Username',
          'Referred_By',
          'Monetized_By'
        ];

        return searchFields.some(field => {
          const value = row[field];
          const matches = value && value.toString().toLowerCase().includes(searchLower);
          if (matches) {
            console.log('Found match in field:', field, 'value:', value);
          }
          return matches;
        });
      });
      console.log('After search filter:', filtered.length);
    }

    console.log('Final filtered data:', {
      originalCount: rowData.length,
      filteredCount: filtered.length,
      activeTab,
      hasSearch: !!searchTerm
    });

    return filtered;
  }, [rowData, activeTab, searchTerm, user]);

  // Update onGridReady handler
  const onGridReady = useCallback((params) => {
    setGridApi(params.api);
    setColumnApi(params.columnApi);
    
    if (params.api) {
      params.api.sizeColumnsToFit();
    }
  }, []);

  // Update effect to handle filtered data changes
  useEffect(() => {
    if (gridApi && filteredData) {
      gridApi.setGridOption('rowData', filteredData);
    }
  }, [gridApi, filteredData]);

  // Remove the duplicate useEffect for filtering data
  useEffect(() => {
    console.log('Filtering data:', { activeTab, rowDataLength: rowData?.length });
    
    if (!rowData || !Array.isArray(rowData)) {
        console.log('No row data available or invalid format');
        return;
    }
  }, [activeTab, rowData]);

  // Check admin status based on user role
  useEffect(() => {
    if (user?.role) {
      setIsAdmin(user.role === 'admin');
      console.log('Admin status:', user.role === 'admin', 'Role:', user.role);
    } else {
      setIsAdmin(false);
    }
  }, [user]);

  // Handle status changes (checkboxes)
  const handleStatusChange = async (leadId, field, value) => {
    try {
      // If marking as monetized, show confirmation dialog
      if (field === 'Snap_Monetized' && value === true) {
        // Create a custom modal dialog with Yes/No buttons
        const shouldNotify = await new Promise((resolve) => {
          const dialog = document.createElement('div');
          dialog.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10000;
          `;
          
          const content = document.createElement('div');
          content.style.cssText = `
            background: white;
            padding: 30px;
            border-radius: 8px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            max-width: 400px;
            text-align: center;
          `;
          
          const title = document.createElement('h3');
          title.textContent = 'Do you want to notify the client?';
          title.style.cssText = 'margin: 0 0 20px 0; font-size: 18px; color: #333;';
          
          const buttonContainer = document.createElement('div');
          buttonContainer.style.cssText = 'display: flex; gap: 10px; justify-content: center; margin-top: 20px;';
          
          const yesButton = document.createElement('button');
          yesButton.textContent = 'Yes';
          yesButton.style.cssText = `
            padding: 10px 30px;
            background: #4CAF50;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 16px;
            font-weight: 500;
          `;
          yesButton.onmouseover = () => yesButton.style.background = '#45a049';
          yesButton.onmouseout = () => yesButton.style.background = '#4CAF50';
          yesButton.onclick = () => {
            document.body.removeChild(dialog);
            resolve(true);
          };
          
          const noButton = document.createElement('button');
          noButton.textContent = 'No';
          noButton.style.cssText = `
            padding: 10px 30px;
            background: #f44336;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 16px;
            font-weight: 500;
          `;
          noButton.onmouseover = () => noButton.style.background = '#da190b';
          noButton.onmouseout = () => noButton.style.background = '#f44336';
          noButton.onclick = () => {
            document.body.removeChild(dialog);
            resolve(false);
          };
          
          buttonContainer.appendChild(yesButton);
          buttonContainer.appendChild(noButton);
          content.appendChild(title);
          content.appendChild(buttonContainer);
          dialog.appendChild(content);
          document.body.appendChild(dialog);
        });
        
        const updateData = {
          [field]: value,
          send_monetization_notification: shouldNotify
        };
        
        console.log('Sending monetization status update:', {
          leadId,
          field,
          value,
          shouldNotify,
          updateData
        });
        
        // Update UI optimistically
        setRowData(prevData => 
          prevData.map(row => 
            row.client_id === leadId ? { ...row, [field]: value } : row
          )
        );

        // Use the correct API endpoint with client_id
        const response = await axios.put(`${API_BASE_URL}/api/leads/${leadId}`, updateData, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (response.data.status === 'success') {
          console.log('Update successful');
          if (gridApi) {
            gridApi.refreshCells({ force: true });
          }
        } else {
          // Revert on failure
          fetchLeads();
        }
      } else {
        // For all other fields, proceed normally
        const updateData = {
          [field]: value
        };
        
        console.log('Sending status update:', {
          leadId,
          field,
          value,
          updateData
        });
        
        // Update UI optimistically
        setRowData(prevData => 
          prevData.map(row => 
            row.client_id === leadId ? { ...row, [field]: value } : row
          )
        );

        // Use the correct API endpoint with client_id
        const response = await axios.put(`${API_BASE_URL}/api/leads/${leadId}`, updateData, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (response.data.status === 'success') {
          console.log('Update successful');
          if (gridApi) {
            gridApi.refreshCells({ force: true });
          }
        } else {
          // Revert on failure
          fetchLeads();
        }
      }

    } catch (error) {
      console.error('Error updating status:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data
      });
      // Revert the optimistic update on error
      fetchLeads();
    }
  };

  const gridOptions = useMemo(() => ({
    defaultColDef: {
      sortable: true,
      filter: true,
      resizable: true,
      suppressSizeToFit: false,
      cellStyle: { 
        display: 'flex',
        alignItems: 'center'
      }
    },
    getRowId: params => params.data?.client_id || params.data?.id,
    deltaRowDataMode: true,
    suppressScrollOnNewData: true,
    rowClass: params => params.node.rowIndex % 2 === 0 ? 'ag-row-even' : 'ag-row-odd',
    theme: 'ag-theme-alpine',
    suppressColumnVirtualisation: true,
    suppressCellFocus: false,
    rowHeight: 48,
    headerHeight: 48,
    rowSelection: 'single',
    animateRows: true,
    domLayout: 'normal',
    onFirstDataRendered: (params) => {
      params.api.sizeColumnsToFit();
    }
  }), []);

  const columns = [
    {
      headerName: 'CLIENT INFO',
      headerClass: 'centered-header',
      children: [
        {
          headerName: 'ACTIONS',
          field: 'actions',
          width: 300,
          sortable: false,
          filter: false,
          pinned: 'left',
          cellRenderer: params => {
            return (
              <div className="action-buttons">
                <button className="edit-button" title="Edit" onClick={() => handleEdit(params.data)}>
                  <FontAwesomeIcon icon={faPencilAlt} />
                </button>
                <button title="Contract" onClick={(e) => handleContractClick(params.data)}>
                  <FontAwesomeIcon icon={faFileSignature} />
                </button>
                <button className="note-button1" title="Notes" onClick={(e) => {
                  e.stopPropagation();
                  saveGridScrollPosition();
                  setSelectedClient({
                    client_id: params.data.client_id,
                    name: `${params.data.First_Legal_Name || ''} ${params.data.Last_Legal_Name || ''}`.trim()
                  });
                  setNotesModalOpen(true);
                }}>
                  <FontAwesomeIcon icon={faNoteSticky} />
                </button>
                <button className="social-links-button" title="Social Links" onClick={(e) => {
                  e.stopPropagation();
                  saveGridScrollPosition();
                  setSelectedSocialData(params.data);
                  setSocialLinksModalOpen(true);
                }}>
                  <FontAwesomeIcon icon={faShare} />
                </button>
                <button 
                  className="google-meet-button"
                  title="Schedule Google Meet"
                  onClick={(e) => {
                    e.stopPropagation();
                    saveGridScrollPosition();
                    setSelectedMeetClient(params.data);
                    setGoogleMeetModalOpen(true);
                  }}
                >
                  <FontAwesomeIcon icon={faPhone} />
                </button>
                <button 
                  className="tiktok-download-button"
                  title="Download TikToks"
                  onClick={(e) => {
                    e.stopPropagation();
                    saveGridScrollPosition();
                    setSelectedTiktokClient(params.data);
                    setTiktokModalOpen(true);
                  }}
                >
                  <FontAwesomeIcon icon={faDownload} />
                </button>
                <button className="delete-button" title="Delete" onClick={(e) => {
                  e.stopPropagation();
                  setLeadToDelete(params.data);
                  setShowDeleteConfirm(true);
                }}>
                  <FontAwesomeIcon icon={faTrashAlt} />
                </button>
              </div>
            );
          }
        }
      ]
    },
    {
      headerName: 'Rank',
      headerClass: 'centered-header',
      children: [
        {
          headerName: 'SCORE',
          field: 'SCORE',
          width: 100,
          pinned: 'left',
          sort: 'desc',
          sortable: true,
          valueGetter: (params) => {
            const id = params.data?._id || params.data?.id;  // Try both _id and id
            const clientId = params.data?.client_id;
            return rankData[id] || rankData[clientId] || 0;  // Try both IDs for score lookup
          },
          cellRenderer: params => {
            const score = params.value;
            let backgroundColor;
            
            if (score <= 30) backgroundColor = '#ffcdd2';
            else if (score <= 50) backgroundColor = '#ffe6e6';
            else if (score <= 60) backgroundColor = '#e6ffe6';
            else if (score <= 80) backgroundColor = '#ccffcc';
            else backgroundColor = '#99ff99';

            return (
              <div 
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor,
                  color: '#000000'
                }}
              >
                {score || 'N/A'}
              </div>
            );
          }
        }
      ]
    },
    // Basic Info Group
    {
      headerName: 'Basic Info',
      headerClass: 'centered-header',
      children: [
        {
          headerName: 'LEGAL NAME',
          valueGetter: params => {
            const firstName = params.data?.First_Legal_Name || '';
            const lastName = params.data?.Last_Legal_Name || '';
            return `${firstName} ${lastName}`.trim();
          },
          width: 200,
          pinned: 'left'
        },
        {
          headerName: 'G / S / V / M / D',
          width: 180,
          headerClass: 'centered-header',
          cellStyle: { display: 'flex', justifyContent: 'center' },
          cellRenderer: params => (
            <div className="checkbox-group" style={{ justifyContent: 'center', width: '100%' }}>
              <input 
                type="checkbox" 
                checked={params.data.is_groupchat || false}
                onChange={e => handleStatusChange(params.data.client_id, 'is_groupchat', e.target.checked)}
              />
              <span className="checkbox-separator">/</span>
              <input 
                type="checkbox" 
                checked={params.data.is_signed || false}
                onChange={e => handleStatusChange(params.data.client_id, 'is_signed', e.target.checked)}
              />
              <span className="checkbox-separator">/</span>
              <input 
                type="checkbox" 
                checked={params.data.Snap_Star || false}
                onChange={e => handleStatusChange(params.data.client_id, 'Snap_Star', e.target.checked)}
              />
              <span className="checkbox-separator">/</span>
              <input 
                type="checkbox" 
                checked={params.data.Snap_Monetized || false}
                onChange={e => handleStatusChange(params.data.client_id, 'Snap_Monetized', e.target.checked)}
              />
              <span className="checkbox-separator">/</span>
              <input 
                type="checkbox" 
                checked={params.data.is_dead || false}
                onChange={e => handleStatusChange(params.data.client_id, 'is_dead', e.target.checked)}
              />
            </div>
          ),
          headerComponent: () => (
            <div className="checkbox-header" style={{ justifyContent: 'center', width: '100%', display: 'flex' }}>
              G <span>/</span> S <span>/</span> V <span>/</span> M <span>/</span> D
            </div>
          )
        },
        {
          headerName: 'Onboarding Progress',
          field: 'onboarding_progress',
          width: 220,
          cellStyle: { padding: '4px' },
          autoHeight: true,
          headerComponent: () => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
              <span>Onboarding Progress</span>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <div 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowOnboardingText(!showOnboardingText);
                  }}
                  style={{
                    width: '24px',
                    height: '12px',
                    backgroundColor: showOnboardingText ? '#3b82f6' : '#d1d5db',
                    borderRadius: '8px',
                    position: 'relative',
                    transition: 'background-color 0.2s',
                    cursor: 'pointer'
                  }}
                >
                  <div 
                    style={{
                      width: '8px',
                      height: '8px',
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      position: 'absolute',
                      top: '2px',
                      left: showOnboardingText ? '14px' : '2px',
                      transition: 'left 0.2s',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                    }}
                  />
                </div>
              </label>
            </div>
          ),
          cellRenderer: params => {
            const clientId = params.data?.client_id;
            const clientName = `${params.data?.First_Legal_Name || ''} ${params.data?.Last_Legal_Name || ''}`.trim();
            const statusData = onboardingStatuses[clientId];
            
            return (
              <OnboardingProgressBar
                clientId={clientId}
                statusData={statusData}
                showWaitingText={showOnboardingText}
                onClick={(cId) => {
                  setSelectedOnboardingClient({ id: cId, name: clientName });
                  setOnboardingModalOpen(true);
                }}
              />
            );
          }
        },
        {
          headerName: 'Referred By',
          field: 'referred_by_name',
          width: 175,
          cellRenderer: (params) => partnerCellRenderer(params, 'referred')
        },
        {
          headerName: 'Monetized By',
          field: 'monetized_by_name',
          flex: 1,
          minWidth: 175,
          cellRenderer: (params) => partnerCellRenderer(params, 'monetized')
        }
      ]
    },
    // Info Group
    {
      headerName: 'Info',
      children: [
        {
          headerName: 'Email',
          field: 'Email_Address',
          width: 250,
          hide: false
        },
        {
          headerName: 'Client ID',
          field: 'client_id',
          width: 150,
        },
        {
          headerName: 'Snap ID',
          field: 'snap_id',
          width: 150,
        }
      ]
    },
    // Add this new group after the Info group
    {
      headerName: 'Social',
      children: [
        {
          headerName: 'Snapchat',
          field: 'Snap_Username',
          width: 150,
          hide: true
        }
      ]
    },
    // Created column - hidden
    {
      headerName: 'Created',
      field: 'created_at',
      hide: true
    }
  ];

  const fetchLeadsData = useCallback(async () => {
    try {
      console.log('Fetching leads data...');
      // Use the leads-only endpoint
      const response = await axios.get(`${API_BASE_URL}/api/leads/raw`);
      setLeadsData(response.data);
      console.log('Leads data fetched:', response.data.length, response.data);
    } catch (error) {
      console.error('Error fetching leads data:', error);
    }
  }, []);

  const fetchOnboardingStatuses = async (clientIds) => {
    try {
      console.log('Fetching onboarding statuses for', clientIds.length, 'clients');
      const response = await axios.post(`${API_BASE_URL}/api/lead/onboarding-status/bulk`, {
        client_ids: clientIds
      });
      console.log('Onboarding API response:', response.data);
      console.log('First 3 clients:', Object.keys(response.data).slice(0, 3).map(cid => ({
        client_id: cid,
        current_stage: response.data[cid]?.current_stage,
        current_stage_name: response.data[cid]?.current_stage_name
      })));
      setOnboardingStatuses(response.data);
      console.log('Onboarding statuses fetched');
    } catch (error) {
      console.error('Error fetching onboarding statuses:', error);
    }
  };

  const fetchLeads = useCallback(async () => {
    setIsLoading(true);
    try {
      console.log('Starting to fetch leads...');
      
      // Get leads with essential data in a single call
      const leadsResponse = await axios.get(API_ENDPOINTS.LEADS.GRID);
      const leads = leadsResponse.data;
      
      // Only update the grid when data actually changes
      const hasDataChanged = (newData, currentData) => {
        if (!Array.isArray(newData) || !Array.isArray(currentData)) return true;
        if (newData.length !== currentData.length) return true;
        const map = new Map(currentData.map(r => [(r.client_id || r.id), r]));
        for (const n of newData) {
          const key = n.client_id || n.id;
          const o = map.get(key);
          if (!o) return true;
          // Compare a subset of fields that affect rendering/sorting
          if (
            (o.SCORE !== n.SCORE) ||
            (o.is_groupchat !== n.is_groupchat) ||
            (o.is_signed !== n.is_signed) ||
            (o.Snap_Star !== n.Snap_Star) ||
            (o.Snap_Monetized !== n.Snap_Monetized) ||
            (o.is_dead !== n.is_dead) ||
            (o.referred_by_name !== n.referred_by_name) ||
            (o.monetized_by_name !== n.monetized_by_name)
          ) {
            return true;
          }
        }
        return false;
      };
      
      // Only update if data actually changed
      if (hasDataChanged(leads, rowData)) {
        // Calculate current top row based on scroll position
        let topRow = null;
        if (gridApi) {
          const viewport = document.querySelector('.ag-center-cols-viewport') || document.querySelector('.ag-body-viewport');
          if (viewport && viewport.scrollTop > 0) {
            const rowHeight = gridApi.getSizesForCurrentTheme().rowHeight || 42;
            topRow = Math.floor(viewport.scrollTop / rowHeight);
          } else {
            topRow = 0;
          }
        }
        setRowData(leads);
        
        // Fetch onboarding statuses for all clients
        const clientIds = leads.map(lead => lead.client_id).filter(Boolean);
        if (clientIds.length > 0) {
          fetchOnboardingStatuses(clientIds);
        }
        
        setTimeout(() => {
          if (gridApi != null && topRow != null && topRow >= 0) {
            gridApi.ensureIndexVisible(topRow, 'top');
          }
        }, 0);
        console.log('Lead data updated - changes detected');
      } else {
        console.log('No significant changes in lead data - skipping update');
      }
      
      // Start fetching ranks data in the background (less frequently)
      const fetchRanksAndAnalytics = async () => {
        try {
          const ranksResponse = await axios.get(API_ENDPOINTS.LEADS.RANKS);
          const ranks = {};
          ranksResponse.data.forEach(item => {
            if (item.id) {
              ranks[item.id] = item.score;
              if (item.client_id) {
                ranks[item.client_id] = item.score;
              }
            }
          });
          
          // Only update ranks if they've changed
          const ranksChanged = JSON.stringify(ranks) !== JSON.stringify(rankData);
          if (ranksChanged) {
            setRankData(ranks);
            console.log('Rank data updated - changes detected');
          }
          
          // Get client IDs for analytics (only if we have new leads)
          if (hasDataChanged(leads, rowData)) {
            const clientIds = leads
              .map(lead => lead.client_id)
              .filter(Boolean);
              
            if (clientIds.length > 0) {
              const analyticsResponse = await axios.get(API_ENDPOINTS.ANALYTICS.GET_MOBILE, {
                params: { user_ids: clientIds.join(',') }
              });
              
              if (analyticsResponse.data?.status === 'success') {
                setTotalViews(prev => {
                  const newViews = {
                    all: analyticsResponse.data.total_views || 0,
                    filtered: analyticsResponse.data.total_views || 0,
                    loading: false
                  };
                  
                  // Only update if views have changed significantly (> 5% change)
                  const viewsChanged = Math.abs(newViews.all - prev.all) / Math.max(prev.all, 1) > 0.05;
                  return viewsChanged ? newViews : prev;
                });
              }
            }
          }
        } catch (error) {
          console.error('Error fetching additional data:', error);
        }
      };
      
      // Start background data fetch
      fetchRanksAndAnalytics();
      
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to load leads data');
    } finally {
      setIsLoading(false);
    }
  }, [rowData, rankData]);

  // Update useEffect to avoid redundant calls
  useEffect(() => {
    fetchLeads();
    fetchLeadsData();
    const refreshInterval = setInterval(() => {
      fetchLeads();
      fetchLeadsData();
    }, 30 * 60 * 1000); // Changed from 5 to 30 minutes
    return () => clearInterval(refreshInterval);
  }, []); // Remove fetchLeads from dependency array to prevent infinite loop

  // Add title effect
  useEffect(() => {
    document.title = 'Leads | Snapped';
  }, []);

  // Add effect to redirect users away from views they don't have access to
  useEffect(() => {
    if (user?.role && currentView) {
      // Check if current view requires a feature check
      const viewFeatureMap = {
        'DASHBOARD': FEATURES.DASHBOARD,
        'VIEW_LEADS': FEATURES.CLIENTS,
        'UPLOADS': FEATURES.UPLOADS,
        'TASKS': FEATURES.TASKS,
        'EMPLOYEE_DASHBOARD': FEATURES.EMPLOYEES,
        'ANALYTICS': FEATURES.ANALYTICS,
        'ADMIN_SURVEY': FEATURES.SURVEY,
        'TASK_TEMPLATES': FEATURES.TASK_TEMPLATES,
        'RFP_MANAGER': FEATURES.RFP_MANAGER,
        'MASS_MESSAGING': FEATURES.MASS_MESSAGING,
        'TIKTOK_VIEWER': FEATURES.TIKTOK_VIEWER,
        'NOTIFICATIONS': FEATURES.NOTIFICATIONS,
        'NOTIFICATIONS_SETUP': FEATURES.NOTIFICATIONS
      };

      const requiredFeature = viewFeatureMap[currentView];
      
      // If this view requires a feature and user doesn't have access, redirect
      if (requiredFeature && !canAccessFeature(user.role, requiredFeature)) {
        console.log(`User role ${user.role} does not have access to ${currentView}, redirecting...`);
        setCurrentView(getDefaultView());
      }
    }
  }, [user?.role, currentView, getDefaultView]);

  const toggleFullscreen = () => {
    const element = document.querySelector('.lead-tracker-view');
    
    if (!document.fullscreenElement) {
      // Enter fullscreen
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  // Add fullscreen change event listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  const openSocialLinks = (data) => {
    saveGridScrollPosition();
    setSelectedLead(data);
    setModalOpen(true);
  };

  const handleEdit = (data) => {
    saveGridScrollPosition();
    console.log('Edit button clicked, data:', data);
    console.log('Current view before:', currentView);
    setCurrentView('VIEW_LEADS');
    setSelectedLead(data);
    console.log('Selected lead set to:', data);
    setEditModalOpen(true);
    console.log('Edit modal should be open now');
  };

  const handleSave = async (updatedData) => {
    try {
        console.log('Starting update with data:', updatedData);
        
        // Get the auth token using fetchAuthSession
        let authToken;
        try {
            const { tokens } = await fetchAuthSession();
            authToken = tokens.accessToken.toString();
        } catch (error) {
            console.error('Error getting auth token:', error);
            toast.error('Authentication error');
            return;
        }
        
        // Use client_id for the update
        const clientId = updatedData.client_id;
        if (!clientId) {
            console.error('No client_id found in updatedData:', updatedData);
            toast.error('Missing client ID');
            return;
        }

        // Log the request details
        const updateUrl = API_ENDPOINTS.LEADS.UPDATE(clientId);
        console.log('Making request to:', updateUrl);
        console.log('With data:', JSON.stringify(updatedData, null, 2));
        console.log('Auth token present:', !!authToken);
        
        const response = await axios.put(
            updateUrl,
            updatedData,
            {
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                }
            }
        );
        
        console.log('Received response:', response.data);
        
        if (response.data && response.data.status === "success") {
            // Update the row data in place
            setRowData(prevData => 
                prevData.map(row => 
                    row.client_id === clientId ? updatedData : row
                )
            );
            
            // Update grid if it exists
            if (gridApi) {
                // Ensure updatedData has required fields for AG Grid
                if (updatedData && updatedData.client_id) {
                    const transaction = {
                        update: [updatedData]
                    };
                    try {
                        gridApi.applyTransaction(transaction);
                    } catch (gridError) {
                        console.warn('AG Grid transaction failed, data still updated in state:', gridError);
                    }
                } else {
                    console.error('Updated data missing client_id, skipping grid update:', updatedData);
                }
            }

            // Show success message
            toast.success('Lead updated successfully');
        } else {
            console.error('Update failed:', response.data);
            toast.error(response.data?.message || 'Failed to update lead');
        }
    } catch (error) {
        console.error('Error updating lead:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
        console.error('Error details:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
            config: error.config
        });
        toast.error(error.response?.data?.detail || 'Error updating lead');
    }
};

  const handleDelete = async (leadId) => {
    try {
      // Get auth token
      const { tokens } = await fetchAuthSession();
      const token = tokens.idToken.toString();

      // Delete the lead using client_id
      const response = await axios.delete(API_ENDPOINTS.LEADS.DELETE(leadId), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.status === 'success' || response.status === 200) {
        // Remove the lead from the correct data source
        if (activeTab === 'Leads') {
          setLeadsData(prevData => prevData.filter(row => row.client_id !== leadId));
          // Refresh leads data
          await fetchLeadsData();
        } else {
          setRowData(prevData => prevData.filter(row => row.client_id !== leadId));
          // Refresh client data
          await fetchLeads();
        }
        
        toast.success('Lead deleted successfully');
      } else {
        toast.error('Failed to delete lead');
      }
      
      // Close the confirmation modal
      setShowDeleteConfirm(false);
      setLeadToDelete(null);
    } catch (error) {
      console.error('Error deleting lead:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Error deleting lead';
      toast.error(errorMessage);
      setShowDeleteConfirm(false);
      setLeadToDelete(null);
    }
  };

  useEffect(() => {
    if (gridApi) {
      console.log('Grid API initialized');
      console.log('Grid classes:', document.querySelector('.ag-theme-alpine').classList);
      
      // Force refresh of the grid
      gridApi.refreshCells({
        force: true,
        columns: ['Performance_Score']
      });
    }
  }, [gridApi]);

  // Add this function to handle opening the add lead modal
  const handleAddLead = () => {
    setCurrentView('VIEW_LEADS');
    setSelectedLead({}); // Empty data for new lead
    setEditModalOpen(true);
  };

  const handleNavClick = (view) => {
    setCurrentView(view);
    if (view === 'ADD_LEAD') {
      handleAddLead();
    }
  };

  const handleContractClick = (clientData) => {
    saveGridScrollPosition();
    console.log('Opening contract for client:', clientData); // Debug log
    setSelectedContractClient({
      ...clientData,
      id: clientData._id || clientData.id || clientData.client_id, // Handle all possible ID fields
      name: `${clientData.First_Legal_Name || ''} ${clientData.Last_Legal_Name || ''}`.trim(),
      email: clientData.Email_Address,
      // Add any other required fields for the contract
    });
    setContractModalOpen(true);
  };

  const handleTikTokDownload = async (tiktokUrl) => {
    try {
      const response = await axios.post(API_ENDPOINTS.TIKTOK.DOWNLOAD, {
        client_id: selectedTiktokClient.client_id,
        tiktok_url: tiktokUrl
      });
      
      // Handle new background processing response
      if (response.data.status === 'processing_started') {
        console.log('TikTok processing started in background');
        return response.data;  // Return the response data
      } else if (response.data.status === 'success') {
        // Handle legacy success response if still present
        console.log('TikToks downloaded successfully');
        return response.data;
      } else {
        throw new Error('Unexpected response format');
      }
    } catch (error) {
      console.error('Error starting TikTok download:', error);
      throw new Error('Failed to start TikTok download');
    }
  };

  // Add state for download progress
  const [downloadProgress, setDownloadProgress] = useState(null);

  const fetchPartners = async () => {
    try {
      setPartnersLoading(true);
      const response = await axios.get(API_ENDPOINTS.PARTNERS.LIST);
      console.log('Partners response:', response.data);
      
      // Ensure we handle both array and object responses
      const partnersData = Array.isArray(response.data) ? response.data : [];
      
      setPartners(partnersData);
    } catch (error) {
      console.error('Error fetching partners:', error);
      setError('Failed to load partners');
    } finally {
      setPartnersLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  // Handle partner employee assignments updates
  const handlePartnerEmployeeAssignments = (assignments) => {
    setPartnerEmployeeAssignments(assignments);
  };

  // Simple: Remove old partner employees, add new partner employees
  const handlePartnerChangeWithEmployees = async (clientId, newPartnerId, oldPartnerId, type) => {
    const updateProcessingState = (step, current, total) => {
      setProcessingState({
        loading: true,
        currentStep: step,
        progress: { current, total }
      });
    };

    try {
      const { tokens } = await fetchAuthSession();
      
      console.log(`${type} partner changed from ${oldPartnerId} to ${newPartnerId} for client ${clientId}`);
      
      // Step 1: Get client data
      updateProcessingState('Getting current client data...', 1, 5);
      const clientResponse = await axios.get(API_ENDPOINTS.LEADS.GRID, {
        headers: { 'Authorization': `Bearer ${tokens.accessToken.toString()}` }
      });
      
      // Find this specific client
      const clientData = clientResponse.data.find(client => client.client_id === clientId);
      if (!clientData) {
        throw new Error(`Could not find client with ID ${clientId}`);
      }
      
      let assignedEmployees = clientData.assigned_employees || [];
      console.log('Current assigned employees:', assignedEmployees);
      
      // Step 2: Handle old partner
      if (oldPartnerId) {
        updateProcessingState('Getting employees to remove...', 2, 5);
        // Get the auto-assigned employees from the old partner
        const oldResponse = await axios.get(`${API_BASE_URL}/api/leads/partners/${oldPartnerId}/employees`, {
          headers: { 'Authorization': `Bearer ${tokens.accessToken.toString()}` }
        });
        
        const oldEmployees = oldResponse.data || [];
        console.log(`Found ${oldEmployees.length} auto-assigned employees to remove from old partner ${oldPartnerId}:`, 
          oldEmployees.map(e => e.user_id));
        
        // Remove these employee IDs from the assigned_employees array
        assignedEmployees = assignedEmployees.filter(id => !oldEmployees.find(e => e.user_id === id));
        
        // Also remove the client from each employee's clients array
        updateProcessingState('Removing old employee assignments...', 3, 5);
        for (const employee of oldEmployees) {
          try {
            await axios.delete(API_ENDPOINTS.EMPLOYEES.UNASSIGN, {
              data: { client_id: clientId, user_id: employee.user_id },
              headers: { 'Authorization': `Bearer ${tokens.accessToken.toString()}` }
            });
            console.log(`Removed client ${clientId} from employee ${employee.user_id}'s record`);
          } catch (error) {
            console.error(`Error removing client from employee ${employee.user_id}'s record:`, error);
          }
        }
      }
      
      // Step 3: Handle new partner
      if (newPartnerId) {
        updateProcessingState('Getting new employees to add...', 3, 5);
        // Get the auto-assigned employees from the new partner
        const newResponse = await axios.get(`${API_BASE_URL}/api/leads/partners/${newPartnerId}/employees`, {
          headers: { 'Authorization': `Bearer ${tokens.accessToken.toString()}` }
        });
        
        const newEmployees = newResponse.data || [];
        console.log(`Found ${newEmployees.length} auto-assigned employees to add from new partner ${newPartnerId}:`,
          newEmployees.map(e => e.user_id));
        
        // Add these employee IDs to the assigned_employees array (if not already there)
        updateProcessingState('Adding new employee assignments...', 4, 5);
        for (const employee of newEmployees) {
          if (!assignedEmployees.includes(employee.user_id)) {
            assignedEmployees.push(employee.user_id);
            
            // Also add the client to the employee's clients array
            try {
              await axios.post(API_ENDPOINTS.EMPLOYEES.ASSIGN, {
                client_id: clientId,
                user_id: employee.user_id
              }, {
                headers: { 'Authorization': `Bearer ${tokens.accessToken.toString()}` }
              });
              console.log(`Added client ${clientId} to employee ${employee.user_id}'s record`);
            } catch (error) {
              console.error(`Error adding client to employee ${employee.user_id}'s record:`, error);
            }
          }
        }
      }
      
      // Step 4: Update client and refresh
      updateProcessingState('Updating client and refreshing data...', 5, 5);
      console.log('Updating client with new assigned_employees:', assignedEmployees);
      await axios.put(API_ENDPOINTS.LEADS.UPDATE(clientId), {
        assigned_employees: assignedEmployees
      }, {
        headers: { 'Authorization': `Bearer ${tokens.accessToken.toString()}` }
      });
      
      // Refresh the grid to show changes
      await fetchLeads();
      
    } catch (error) {
      console.error('Error updating partner employees:', error);
      alert('Failed to update partner employee assignments: ' + error.message);
    } finally {
      setProcessingState({
        loading: false,
        currentStep: '',
        progress: { current: 0, total: 0 }
      });
    }
  };

  const syncAllPartnerEmployees = async (clientId, clientData) => {
    /**
     * Syncs employee assignments to match BOTH referred_by and monetized_by partners.
     * This ensures the client has exactly the employees from their assigned partners.
     */
    const updateProcessingState = (step, current, total) => {
      setProcessingState({
        loading: true,
        currentStep: step,
        progress: { current, total }
      });
    };

    try {
      const { tokens } = await fetchAuthSession();
      
      console.log(`Syncing all partner employees for client ${clientId}`);
      
      // Step 1: Collect all partner IDs
      updateProcessingState('Identifying partners...', 1, 4);
      const partnerIds = [];
      if (clientData.referred_by) partnerIds.push(clientData.referred_by);
      if (clientData.monetized_by) partnerIds.push(clientData.monetized_by);
      
      if (partnerIds.length === 0) {
        console.log('No partners assigned to sync employees from');
        return { success: false, added: 0, removed: 0 };
      }
      
      console.log('Partner IDs to sync:', partnerIds);
      
      // Step 2: Get all employees from all partners
      updateProcessingState('Fetching partner employees...', 2, 4);
      const allPartnerEmployees = new Set();
      
      for (const partnerId of partnerIds) {
        try {
          const response = await axios.get(`${API_BASE_URL}/api/leads/partners/${partnerId}/employees`, {
            headers: { 'Authorization': `Bearer ${tokens.accessToken.toString()}` }
          });
          
          const employees = response.data || [];
          console.log(`Found ${employees.length} employees for partner ${partnerId}`);
          employees.forEach(emp => {
            if (emp.user_id) allPartnerEmployees.add(emp.user_id);
          });
        } catch (error) {
          console.error(`Error fetching employees for partner ${partnerId}:`, error);
        }
      }
      
      const targetEmployees = Array.from(allPartnerEmployees);
      console.log('Target employee list:', targetEmployees);
      
      // Step 3: Compare with current assignments
      updateProcessingState('Syncing assignments...', 3, 4);
      const currentEmployees = clientData.assigned_employees || [];
      
      // Find employees to add
      const toAdd = targetEmployees.filter(id => !currentEmployees.includes(id));
      // Find employees to remove (those not in any partner)
      const toRemove = currentEmployees.filter(id => !allPartnerEmployees.has(id));
      
      console.log('Employees to add:', toAdd);
      console.log('Employees to remove:', toRemove);
      
      // Add new employees
      for (const userId of toAdd) {
        try {
          await axios.post(API_ENDPOINTS.EMPLOYEES.ASSIGN, {
            client_id: clientId,
            user_id: userId
          }, {
            headers: { 'Authorization': `Bearer ${tokens.accessToken.toString()}` }
          });
          console.log(`Added employee ${userId} to client ${clientId}`);
        } catch (error) {
          console.error(`Error adding employee ${userId}:`, error);
        }
      }
      
      // Remove employees that shouldn't be there
      for (const userId of toRemove) {
        try {
          await axios.delete(API_ENDPOINTS.EMPLOYEES.UNASSIGN, {
            data: { client_id: clientId, user_id: userId },
            headers: { 'Authorization': `Bearer ${tokens.accessToken.toString()}` }
          });
          console.log(`Removed employee ${userId} from client ${clientId}`);
        } catch (error) {
          console.error(`Error removing employee ${userId}:`, error);
        }
      }
      
      // Step 4: Update client record
      updateProcessingState('Updating client record...', 4, 4);
      await axios.put(API_ENDPOINTS.LEADS.UPDATE(clientId), {
        assigned_employees: targetEmployees
      }, {
        headers: { 'Authorization': `Bearer ${tokens.accessToken.toString()}` }
      });
      
      console.log('Employee sync completed successfully for client', clientId);
      return { success: true, added: toAdd.length, removed: toRemove.length };
      
    } catch (error) {
      console.error('Error syncing partner employees:', error);
      throw error;
    }
  };

  const handleSyncAllClients = async () => {
    try {
      // Show processing state immediately
      setProcessingState({
        loading: true,
        currentStep: 'Starting bulk sync...',
        progress: { current: 0, total: 0 }
      });
      
      // Get all clients
      const clientsToSync = rowData.filter(client => 
        client.referred_by || client.monetized_by
      );
      
      if (clientsToSync.length === 0) {
        setProcessingState({
          loading: false,
          currentStep: '',
          progress: { current: 0, total: 0 }
        });
        alert('No clients with partners to sync');
        return;
      }
      
      console.log(`Syncing ${clientsToSync.length} clients with partners`);
      
      let successCount = 0;
      let errorCount = 0;
      let totalAdded = 0;
      let totalRemoved = 0;
      
      // Process in batches to avoid overwhelming the API
      const batchSize = 5;
      for (let i = 0; i < clientsToSync.length; i += batchSize) {
        const batch = clientsToSync.slice(i, i + batchSize);
        
        setProcessingState({
          loading: true,
          currentStep: `Processing clients ${i + 1}-${Math.min(i + batchSize, clientsToSync.length)} of ${clientsToSync.length}...`,
          progress: { current: i, total: clientsToSync.length }
        });
        
        await Promise.all(
          batch.map(async (client) => {
            try {
              const result = await syncAllPartnerEmployees(client.client_id, client);
              if (result.success) {
                successCount++;
                totalAdded += result.added;
                totalRemoved += result.removed;
              }
            } catch (error) {
              errorCount++;
              console.error(`Failed to sync client ${client.client_id}:`, error);
            }
          })
        );
      }
      
      setProcessingState({
        loading: false,
        currentStep: '',
        progress: { current: 0, total: 0 }
      });
      
      // Refresh the grid
      await fetchLeads();
      
      // Show success message
      const message = `Sync complete! ${successCount} clients synced successfully. ` +
        `Added ${totalAdded} assignments, removed ${totalRemoved} assignments.` +
        (errorCount > 0 ? ` ${errorCount} errors occurred.` : '');
      
      alert(message);
      console.log(message);
      
    } catch (error) {
      console.error('Error in bulk sync:', error);
      setProcessingState({
        loading: false,
        currentStep: '',
        progress: { current: 0, total: 0 }
      });
      alert('Failed to sync all clients: ' + error.message);
    }
  };

  const partnerCellRenderer = (params, type) => {
    const partnerId = params.data?.[`${type}_by`] || '';
    const partnerName = params.data?.[`${type}_by_name`] || '';
    const endpoint = type === 'referred' ? API_ENDPOINTS.PARTNERS.REFERRED : API_ENDPOINTS.PARTNERS.MONETIZED;
    
    // If user is not admin, just show the partner name as text
    if (!isAdmin) {
      return (
        <span style={{ padding: '4px' }}>
          {partnerName || 'None'}
        </span>
      );
    }
    
    // Admin users get the dropdown
    return (
      <select
        value={partnerId}
        onChange={async (e) => {
          const newPartnerId = e.target.value;
          const oldPartnerId = partnerId;
          try {
            const clientId = params.data.client_id;
            
            // Get the partner name before making the API call
            const partner = partners.find(p => p.id === newPartnerId);
            const partnerName = partner ? partner.name : '';
            
            // Create updated row data
            const updatedData = { ...params.data };
            updatedData[`${type}_by`] = newPartnerId;
            updatedData[`${type}_by_name`] = partnerName;
            
            // Use AG-Grid transaction update to prevent scroll jumping
            const transaction = {
              update: [updatedData]
            };
            params.api.applyTransaction(transaction);
            
            // Update memory state without triggering refresh
            setRowData(prevData => 
              prevData.map(row => 
                row.client_id === clientId ? updatedData : row
              )
            );
            
            // Make the API call
            await axios.post(endpoint, {
              client_id: clientId,
              partner_id: newPartnerId
            });
            
            // Handle employee assignments based on partner change
            console.log(`About to call handlePartnerChangeWithEmployees for ${type} dropdown`);
            await handlePartnerChangeWithEmployees(clientId, newPartnerId, oldPartnerId, type);
            
          } catch (error) {
            console.error(`Error updating ${type} by:`, error);
            // Revert changes on error
            const transaction = {
              update: [params.data]
            };
            params.api.applyTransaction(transaction);
            
            setRowData(prevData => 
              prevData.map(row => 
                row.client_id === params.data.client_id ? params.data : row
              )
            );
          }
        }}
      >
        <option value="">Select Partner</option>
        {partners.map(partner => (
          <option 
            key={partner.id} 
            value={partner.id}
          >
            {partner.name}
          </option>
        ))}
      </select>
    );
  };

  const fetchRanks = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.LEADS.RANKS);
      const ranks = {};
      response.data.forEach(item => {
        if (item.id) {
          ranks[item.id] = item.score;  // Map using the MongoDB _id
          if (item.client_id) {
            ranks[item.client_id] = item.score;  // Also map using client_id as fallback
          }
        }
      });
      setRankData(ranks);
      
      // Force grid refresh if gridApi exists
      if (gridApi) {
        gridApi.refreshCells({
          force: true,
          columns: ['SCORE']
        });
      }
      
      return ranks;
    } catch (error) {
      console.error('Error fetching ranks:', error);
      return {};
    }
  };

  // Update fetchTotalViews
  const fetchTotalViews = useCallback(async (clientIds) => {
    try {
      setTotalViews(prev => ({ ...prev, loading: true }));
      const { tokens } = await fetchAuthSession();
      const token = tokens.idToken.toString();

      // If no client IDs, return zero views
      if (!clientIds || clientIds.length === 0) {
        setTotalViews({
          all: 0,
          filtered: 0,
          sevenDay: 0,
          sevenDayFiltered: 0,
          loading: false
        });
        return;
      }

      // Filter out any empty or invalid client IDs
      const validClientIds = clientIds.filter(id => id && id.trim() !== '');
      
      // Get analytics for filtered clients
      const response = await axios.get(`${API_BASE_URL}/api/analytics/mobile`, {
        params: { 
          user_ids: validClientIds.join(','),
          days: 7  // Request 7-day view count
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data?.status === 'success') {
        setTotalViews({
          all: response.data.total_views || 0,
          filtered: response.data.total_views || 0,
          sevenDay: response.data.seven_day_views || 0,
          sevenDayFiltered: response.data.seven_day_views || 0,
          loading: false
        });
      } else {
        setTotalViews({
          all: 0,
          filtered: 0,
          sevenDay: 0,
          sevenDayFiltered: 0,
          loading: false
        });
      }

    } catch (error) {
      console.error('Error fetching total views:', error);
      setTotalViews(prev => ({ ...prev, loading: false }));
    }
  }, []);

  // Update the fetchTotalViews effect to use memoized filtered data
  useEffect(() => {
    if (!filteredData.length) return;
    
    const clientIds = filteredData
      .map(row => row.client_id)
      .filter(id => id && id.trim() !== '');
      
    if (clientIds.length) {
      fetchTotalViews(clientIds);
    } else {
      setTotalViews({
        all: 0,
        filtered: 0,
        loading: false
      });
    }
  }, [filteredData, fetchTotalViews]);

  // Update loadData to use the same fetchTotalViews
  const loadData = async () => {
    try {
      setIsLoading(true);
      const { tokens } = await fetchAuthSession();
      const token = tokens.idToken.toString();
      
      const response = await axios.get(`${API_BASE_URL}/api/leads`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data) {
        setRowData(response.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load leads data');
      setTotalViews({ all: 0, filtered: 0, loading: false });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load both leads and ranks together
        const [leadsResponse, ranksResponse] = await Promise.all([
          fetchLeads(),
          fetchRanks()
        ]);
        
        console.log('Ranks loaded:', ranksResponse); // Debug log
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    
    loadData();
  }, []); // Empty dependency array for initial load

  const handleSignOut = async () => {
    try {
      await signOut();
      // Force navigation to login after sign out
      window.location.href = '/login';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Modify the refresh button handler to show loading state
  const handleManualRefresh = async () => {
    try {
      setIsLoading(true); // Add loading indicator
      if (activeTab === 'Leads') {
        await fetchLeadsData();
      } else {
        await fetchLeads();
      }
      await fetchRanks(); // Also refresh ranks
    } finally {
      setIsLoading(false);
    }
  };

  // Add scroll handler to prevent back/forward navigation
  useEffect(() => {
    const preventNavigation = (e) => {
      if (e.target.closest('.google-sheet-container')) {
        e.preventDefault();
      }
    };

    window.addEventListener('wheel', preventNavigation, { passive: false });
    return () => window.removeEventListener('wheel', preventNavigation);
  }, []);

  // Render the search section
  const renderSearchSection = () => (
    <div className="search-container">
      <input
        type="text"
        className="quick-search-input"
        placeholder="Search across all fields..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      {searchTerm && (
        <div className="search-results">
          Found {filteredData.length} results
          {activeTab !== 'All' && ` in ${activeTab}`}
        </div>
      )}
    </div>
  );

  // Render the action buttons
  const renderActionButtons = () => (
    <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handleManualRefresh} className="action-button" disabled={isLoading}>
        <FontAwesomeIcon icon={faSync} spin={isLoading} />
      </button>
      <div className="settings-dropdown" ref={settingsDropdownRef}>
        <button 
          className="action-button"
          onClick={(e) => {
            e.stopPropagation();
            setSettingsDropdownOpen(!settingsDropdownOpen);
          }}
        >
          <FontAwesomeIcon icon={faCog} />
        </button>
        {settingsDropdownOpen && (
          <div className="settings-dropdown-content">
            <button onClick={() => {
              setShowAlgorithmControls(true);
              setSettingsDropdownOpen(false);
            }}>
              <FontAwesomeIcon icon={faCog} /> Algorithm
            </button>
            <button onClick={() => {
              toggleFullscreen();
              setSettingsDropdownOpen(false);
            }}>
              <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
              {isFullscreen ? " Exit Fullscreen" : " Fullscreen"}
            </button>
            <button onClick={() => {
              saveGridScrollPosition();
              setShowContactsModal(true);
              setSettingsDropdownOpen(false);
            }}>
              <FontAwesomeIcon icon={faComments} /> Contacts
            </button>
                        {isAdmin && (
                          <>
                            <button onClick={() => {
                              setShowPartnersModal(true);
                              setSettingsDropdownOpen(false);
                            }}>
                              <FontAwesomeIcon icon={faUsers} /> Partners
                            </button>
                            <button onClick={() => {
                              setShowPartnerEmployeeModal(true);
                              setSettingsDropdownOpen(false);
                            }}>
                              <FontAwesomeIcon icon={faUserTie} /> Partner Employees
                            </button>
                          </>
                        )}
          </div>
        )}
      </div>
    </div>
  );

  const renderCurrentView = () => {
    switch (currentView) {
      case 'DASHBOARD':
        // Check if user has access to dashboard
        if (!canAccessFeature(user?.role, FEATURES.DASHBOARD)) {
          // Redirect editors to their default view
          if (user?.role === 'editor') {
            setCurrentView('UPLOADS');
            return null;
          }
          // Redirect sales to their default view
          if (user?.role === 'sales') {
            setCurrentView('CLIENTS');
            return null;
          }
          // For other roles without access, show uploads
          setCurrentView('UPLOADS');
          return null;
        }
        return (
          <Dashboard 
            filteredData={filteredData} 
            isLoading={isLoading}
            totalViews={totalViews || { all: 0, filtered: 0, loading: false }}
            handleTabClick={handleTabClick}
            setActiveTab={setActiveTab}
            handleNavClick={handleNavClick}
          />
        );
      case 'VIEW_LEADS':
        return (
          <div className="lead-tracker-content">
            <div className="lead-tracker-controls">
              <div className="lead-tracker-tabs">
                <button
                  className={`tab-button ${activeTab === 'All' ? 'active' : ''}`}
                  onClick={() => handleTabClick('All')}
                >
                  All
                </button>
                <button
                  className={`tab-button ${activeTab === 'Leads' ? 'active' : ''}`}
                  onClick={() => handleTabClick('Leads')}
                >
                  Leads
                </button>
                <button
                  className={`tab-button ${activeTab === 'All Signed' ? 'active' : ''}`}
                  onClick={() => handleTabClick('All Signed')}
                >
                  All Signed
                </button>
                <button
                  className={`tab-button ${activeTab === 'Pending Form' ? 'active' : ''}`}
                  onClick={() => handleTabClick('Pending Form')}
                >
                  Pending Form
                </button>
                <button
                  className={`tab-button ${activeTab === 'Unmonetized' ? 'active' : ''}`}
                  onClick={() => handleTabClick('Unmonetized')}
                >
                  Unmonetized
                </button>
                <button
                  className={`tab-button ${activeTab === 'Monetized + Verified' ? 'active' : ''}`}
                  onClick={() => handleTabClick('Monetized + Verified')}
                >
                  Monetized + Verified
                </button>
                <button
                  className={`tab-button ${activeTab === 'Dead' ? 'active' : ''}`}
                  onClick={() => handleTabClick('Dead')}
                >
                  Dead
                </button>
                <button
                  className={`tab-button ${activeTab === 'New' ? 'active' : ''}`}
                  onClick={() => handleTabClick('New')}
                >
                  New
                </button>
                <button
                  className={`tab-button ${activeTab === '7 Days' ? 'active' : ''}`}
                  onClick={() => handleTabClick('7 Days')}
                >
                  7 Days
                </button>
              </div>
              <div className="controls-right">
                <input
                  type="text"
                  className="quick-search-input"
                  placeholder="Search across all fields..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button onClick={handleManualRefresh} className="action-button" disabled={isLoading}>
                  <FontAwesomeIcon icon={faSync} spin={isLoading} />
                </button>
                <div className="settings-dropdown" ref={settingsDropdownRef}>
                  <button 
                    className="action-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSettingsDropdownOpen(!settingsDropdownOpen);
                    }}
                  >
                    <FontAwesomeIcon icon={faCog} />
                  </button>
                  {settingsDropdownOpen && (
                    <div className="settings-dropdown-content">
                      <button onClick={() => {
                        setShowAlgorithmControls(true);
                        setSettingsDropdownOpen(false);
                      }}>
                        <FontAwesomeIcon icon={faCog} /> Algorithm
                      </button>
                      <button onClick={() => {
                        toggleFullscreen();
                        setSettingsDropdownOpen(false);
                      }}>
                        <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
                        {isFullscreen ? " Exit Fullscreen" : " Fullscreen"}
                      </button>
                      <button onClick={() => {
                        saveGridScrollPosition();
                        setShowContactsModal(true);
                        setSettingsDropdownOpen(false);
                      }}>
                        <FontAwesomeIcon icon={faComments} /> Contacts
                      </button>
                      {isAdmin && (
                        <>
                          <button onClick={() => {
                            saveGridScrollPosition();
                            setShowPartnersModal(true);
                            setSettingsDropdownOpen(false);
                          }}>
                            <FontAwesomeIcon icon={faUsers} /> Partners
                          </button>
                          <button onClick={() => {
                            saveGridScrollPosition();
                            setShowPartnerEmployeeModal(true);
                            setSettingsDropdownOpen(false);
                          }}>
                            <FontAwesomeIcon icon={faUserTie} /> Partner Employees
                          </button>
                          <button onClick={async () => {
                            setSettingsDropdownOpen(false);
                            await handleSyncAllClients();
                          }}>
                            <FontAwesomeIcon icon={faRotate} /> Sync All Partner Employees
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div 
              className={`ag-theme-alpine lead-tracker-grid ${isFullscreen ? 'fullscreen' : ''}`}
              style={{ 
                height: '80vh',
                width: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                marginTop: '16px'
              }}
            >
              <AgGridReact
                modules={[ClientSideRowModelModule]}
                columnDefs={columns}
                rowData={filteredData}
                {...gridOptions}
                onGridReady={onGridReady}
                pagination={true}
                paginationPageSize={100}
                context={{ openSocialLinks, handleStatusChange }}
              />
            </div>
          </div>
        );
      case 'TASKS':
        return <TaskGrid />;
      case 'UPLOADS':
        return <UploadTracker />;
      case 'ANALYTICS':
        return <AnalyticsDashboard />;
      case 'EMPLOYEE_DASHBOARD':
        return <EmployeeDashboard />;
      case 'PRE_LEADS':
        return isAdmin ? <PreLeadsSheet /> : null;
      case 'ONBOARDING':
        return isAdmin ? <OnboardingSheet /> : null;
      case 'SYSTEMS':
        return isAdmin ? <SystemsSheet /> : null;
      case 'TALENT_PAYOUTS':
        return isAdmin ? <TalentPayoutsSheet /> : null;
      case 'ADMIN_SURVEY':
        return <AdminSurvey />;
      case 'TASK_TEMPLATES':
        return <TaskTemplate />;
      case 'RFP_MANAGER':
        return (
          <RFPManager />
        );
      case 'FORMS_MANAGER':
        return (
          <FormsManager />
        );
      case 'MASS_MESSAGING':
        return <MassMessaging />;
      case 'TIKTOK_VIEWER':
        return <TikTokViewer />;
      case 'NOTIFICATIONS':
        return <NotificationsPage />;
      case 'NOTIFICATIONS_SETUP':
        return <StaffNotificationManager />;
      case 'AUTOMATIONS':
        return <AutomationManager />;
      case 'ROLE_PERMISSIONS_MANAGER':
        return <RolePermissionsManager />;
      default:
        return null;
    }
  };

  // Grid configuration
  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    minWidth: 100,
    flex: 1
  }), []);

  // Add window resize handler
  useEffect(() => {
    const handleResize = () => {
      if (gridApi) {
        gridApi.sizeColumnsToFit();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [gridApi]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setMoreMenuOpen(false);
        setDocsMenuOpen(false);
      }
      // Only close settings dropdown if not clicking on partners modal
      if (settingsDropdownRef.current && !settingsDropdownRef.current.contains(event.target)) {
        const partnersModal = document.querySelector('.partners-modal-overlay');
        if (!partnersModal || !partnersModal.contains(event.target)) {
          setSettingsDropdownOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      {processingState.loading && (
        <div className="processing-overlay">
          <div className="processing-content">
            <FontAwesomeIcon icon={faSpinner} spin />
            <div>{processingState.currentStep}</div>
            {processingState.progress.total > 0 && (
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${(processingState.progress.current / processingState.progress.total) * 100}%` }}
                />
              </div>
            )}
          </div>
        </div>
      )}
      <div className="lead-tracker-view">
      <div className="lead-tracker-navigation">
        <div className="nav-links">
          {canAccessFeature(user?.role, FEATURES.DASHBOARD) && (
            <button 
              className={`nav-link ${currentView === 'DASHBOARD' ? 'active' : ''}`}
              onClick={() => handleNavClick('DASHBOARD')}
            >
              DASHBOARD
            </button>
          )}
          {canAccessFeature(user?.role, FEATURES.CLIENTS) && (
            <button 
              className={`nav-link ${currentView === 'VIEW_LEADS' ? 'active' : ''}`}
              onClick={() => handleNavClick('VIEW_LEADS')}
            >
              CLIENTS
            </button>
          )}
          {canAccessFeature(user?.role, FEATURES.UPLOADS) && (
            <button 
              className={`nav-link ${currentView === 'UPLOADS' ? 'active' : ''}`}
              onClick={() => handleNavClick('UPLOADS')}
            >
              UPLOADS
            </button>
          )}
          {canAccessFeature(user?.role, FEATURES.TASKS) && (
            <button 
              className={`nav-link ${currentView === 'TASKS' ? 'active' : ''}`}
              onClick={() => handleNavClick('TASKS')}
            >
              TASKS
            </button>
          )}
          {/* Only show EMPLOYEES tab for admin and manager users */}
          {canAccessFeature(user?.role, FEATURES.EMPLOYEES) && (
            <button 
              className={`nav-link ${currentView === 'EMPLOYEE_DASHBOARD' ? 'active' : ''}`}
              onClick={() => handleNavClick('EMPLOYEE_DASHBOARD')}
            >
              EMPLOYEES
            </button>
          )}
          {canAccessFeature(user?.role, FEATURES.AUTOMATIONS) && (
            <button 
              className={`nav-link ${currentView === 'AUTOMATIONS' ? 'active' : ''}`}
              onClick={() => handleNavClick('AUTOMATIONS')}
            >
              AUTOMATIONS
            </button>
          )}
          <button 
            className="nav-link"
            onClick={() => setDocsMenuOpen(true)}
          >
            MORE
          </button>
        </div>
      </div>

      {/* Add the docs modal */}
      {docsMenuOpen && (
        <div className="docs-modal-overlay" onClick={() => setDocsMenuOpen(false)}>
          <div className="docs-modal" onClick={e => e.stopPropagation()}>
            <div className="docs-modal-header">
              <h2>More Options</h2>
              <button className="close-button" onClick={() => setDocsMenuOpen(false)}>
                ×
              </button>
            </div>
            <div className="docs-modal-content">
              <div className="docs-grid">
                {canAccessFeature(user?.role, FEATURES.ANALYTICS) && (
                  <div className="docs-grid-item" onClick={() => {
                    handleNavClick('ANALYTICS');
                    setDocsMenuOpen(false);
                  }}>
                    <span>ANALYTICS</span>
                  </div>
                )}
                {canAccessFeature(user?.role, FEATURES.SURVEY) && (
                  <div className="docs-grid-item" onClick={() => {
                    handleNavClick('ADMIN_SURVEY');
                    setDocsMenuOpen(false);
                  }}>
                    <span>SURVEY</span>
                  </div>
                )}
                {canAccessFeature(user?.role, FEATURES.TASK_TEMPLATES) && (
                  <div className="docs-grid-item" onClick={() => {
                    handleNavClick('TASK_TEMPLATES');
                    setDocsMenuOpen(false);
                  }}>
                    <span>TASK TEMPLATES</span>
                  </div>
                )}
                {canAccessFeature(user?.role, FEATURES.RFP_MANAGER) && (
                  <div className="docs-grid-item" onClick={() => {
                    handleNavClick('RFP_MANAGER');
                    setDocsMenuOpen(false);
                  }}>
                    <span>RFP MANAGER</span>
                  </div>
                )}
                {canAccessFeature(user?.role, FEATURES.RFP_MANAGER) && (
                  <div className="docs-grid-item" onClick={() => {
                    handleNavClick('FORMS_MANAGER');
                    setDocsMenuOpen(false);
                  }}>
                    <span>FORMS MANAGER</span>
                  </div>
                )}
                {canAccessFeature(user?.role, FEATURES.MASS_MESSAGING) && (
                  <div className="docs-grid-item" onClick={() => {
                    handleNavClick('MASS_MESSAGING');
                    setDocsMenuOpen(false);
                  }}>
                    <span>MASS MESSAGING</span>
                  </div>
                )}
                {canAccessFeature(user?.role, FEATURES.TIKTOK_VIEWER) && (
                  <div className="docs-grid-item" onClick={() => {
                    handleNavClick('TIKTOK_VIEWER');
                    setDocsMenuOpen(false);
                  }}>
                    <span>TIKTOK VIEWER</span>
                  </div>
                )}
                {canAccessFeature(user?.role, FEATURES.NOTIFICATIONS) && (
                  <div className="docs-grid-item" onClick={() => {
                    handleNavClick('NOTIFICATIONS');
                    setDocsMenuOpen(false);
                  }}>
                    <span>NOTIFICATIONS</span>
                  </div>
                )}
                {canAccessFeature(user?.role, FEATURES.NOTIFICATIONS) && (
                  <div className="docs-grid-item" onClick={() => {
                    handleNavClick('NOTIFICATIONS_SETUP');
                    setDocsMenuOpen(false);
                  }}>
                    <span>EXTERNAL NOTIFICATION SETUP</span>
                  </div>
                )}
                {canAccessFeature(user?.role, FEATURES.ROLE_PERMISSIONS_MANAGER) && (
                  <div className="docs-grid-item" onClick={() => {
                    handleNavClick('ROLE_PERMISSIONS_MANAGER');
                    setDocsMenuOpen(false);
                  }}>
                    <span>ROLE PERMISSIONS</span>
                  </div>
                )}
                {canAccessFeature(user?.role, FEATURES.EMPLOYEES_SHEET) && (
                  <>
                    <div className="docs-grid-item" onClick={() => {
                      handleNavClick('EMPLOYEES');
                      setDocsMenuOpen(false);
                    }}>
                      <span>EMPLOYEES</span>
                    </div>
                    <div className="docs-grid-item" onClick={() => {
                      handleNavClick('PRE_LEADS');
                      setDocsMenuOpen(false);
                    }}>
                      <span>PRE-LEADS</span>
                    </div>
                    <div className="docs-grid-item" onClick={() => {
                      handleNavClick('ONBOARDING');
                      setDocsMenuOpen(false);
                    }}>
                      <span>ONBOARDING</span>
                    </div>
                    <div className="docs-grid-item" onClick={() => {
                      handleNavClick('SYSTEMS');
                      setDocsMenuOpen(false);
                    }}>
                      <span>SYSTEMS</span>
                    </div>
                    <div className="docs-grid-item" onClick={() => {
                      handleNavClick('TALENT_PAYOUTS');
                      setDocsMenuOpen(false);
                    }}>
                      <span>TALENT PAYOUTS</span>
                    </div>
                  </>
                )}
                <div className="docs-grid-item sign-out" onClick={() => {
                  handleSignOut();
                  setDocsMenuOpen(false);
                }}>
                  <span>SIGN OUT</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="lead-tracker-content">
        {renderCurrentView()}
      </div>

      {/* Modals */}
      <SocialLinksModal 
        isOpen={socialLinksModalOpen}
        onClose={() => { setSocialLinksModalOpen(false); setTimeout(restoreGridScrollPosition, 0); }}
        data={selectedSocialData}
      />
      <EditModal
        isOpen={editModalOpen}
        onClose={() => { setEditModalOpen(false); setTimeout(restoreGridScrollPosition, 0); }}
        data={selectedLead}
        onSave={handleSave}
      />
      <NotesModal
        isOpen={notesModalOpen}
        onClose={() => { setNotesModalOpen(false); setTimeout(restoreGridScrollPosition, 0); }}
        clientId={selectedClient?.client_id}
        clientName={selectedClient?.name}
      />
      <ContractModal
        isOpen={contractModalOpen}
        onClose={() => { setContractModalOpen(false); setTimeout(restoreGridScrollPosition, 0); }}
        client={selectedContractClient}
      />
      <TikTokDownloadModal
        isOpen={tiktokModalOpen}
        onClose={() => { setTiktokModalOpen(false); setTimeout(restoreGridScrollPosition, 0); }}
        clientId={selectedTiktokClient?.client_id}
        onDownload={handleTikTokDownload}
      />
      <PartnersModal
        isOpen={showPartnersModal}
        onClose={() => {
          setShowPartnersModal(false);
          // Refresh partners list after modal is closed
          fetchPartners();
          setTimeout(restoreGridScrollPosition, 0);
        }}
        onPartnerAdded={() => {
          // Refresh partners list when a new partner is added
          fetchPartners();
        }}
      />
      <PartnerEmployeeModal
        isOpen={showPartnerEmployeeModal}
        onClose={() => { setShowPartnerEmployeeModal(false); setTimeout(restoreGridScrollPosition, 0); }}
        partners={partners}
        onAssignmentsUpdated={handlePartnerEmployeeAssignments}
      />
      <ContactsModal
        isOpen={showContactsModal}
        onClose={() => { setShowContactsModal(false); setTimeout(restoreGridScrollPosition, 0); }}
      />
      <GoogleMeetModal
        isOpen={googleMeetModalOpen}
        onClose={() => { setGoogleMeetModalOpen(false); setTimeout(restoreGridScrollPosition, 0); }}
        clientData={selectedMeetClient}
      />

      <OnboardingModal
        isOpen={onboardingModalOpen}
        onClose={() => { setOnboardingModalOpen(false); setSelectedOnboardingClient(null); }}
        clientId={selectedOnboardingClient?.id}
        clientName={selectedOnboardingClient?.name}
      />

      {/* Add Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="delete-confirm-modal">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this lead? This action cannot be undone.</p>
            <div className="button-group">
              <button 
                className="cancel-button"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setLeadToDelete(null);
                }}
              >
                Cancel
              </button>
              <button 
                className="delete-button"
                onClick={() => handleDelete(leadToDelete.client_id)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {downloadProgress && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Downloading TikTok Videos</h3>
            <div className="mb-4">
              <div className="text-sm text-gray-600 mb-2">
                {downloadProgress.currentFile}
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full"
                  style={{
                    width: `${(downloadProgress.current / downloadProgress.total) * 100}%`
                  }}
                ></div>
              </div>
              <div className="text-sm text-gray-600 mt-2">
                {downloadProgress.status}
              </div>
            </div>
          </div>
        </div>
      )}
      <AlgorithmControls 
        isOpen={showAlgorithmControls}
        onClose={() => setShowAlgorithmControls(false)}
      />
    </div>
    </>
  );
};

export default LeadTracker; 